/**
 * Blender OBJ/MTL loader for configurable semi-truck rigs. Resolves URLs against Vite base
 * and site root so the same meshes load in dev, preview, and static hosting under `/truck-sim/`.
 */

import * as THREE from "three";
import { MTLLoader } from "three/addons/loaders/MTLLoader.js";
import { OBJLoader } from "three/addons/loaders/OBJLoader.js";

/**
 * Bases of served asset folders (Vite `public/` served at `/` plus `/truck-sim/` in production builds).
 * `FileLoader` does path + name — never pass a leading slash in the *name* when path is set.
 */
const ASSET_PATH_CANDIDATES = [
  /* Vite mirrors `public/` at `/`; dev often has no OBJ at `/truck-sim/…` unless copied into outDir — try `/` first. */
  () => "/",
  () => {
    const b = import.meta.env.BASE_URL || "/";
    return b.endsWith("/") ? b : `${b}/`;
  }
];

function mtlSetPathForUrl(mtlFileUrl) {
  const s = mtlFileUrl;
  if (s.startsWith("http://") || s.startsWith("https://")) {
    return s.slice(0, s.lastIndexOf("/") + 1);
  }
  if (s.startsWith("/")) {
    return s.slice(0, s.lastIndexOf("/") + 1) || "/";
  }
  return s.slice(0, s.lastIndexOf("/") + 1) || "/";
}

/**
 * Tries (mtl+obj) pairs in order; falls back to untextured OBJ if only geometry exists.
 * @returns {Promise<THREE.Object3D|null>} — prepared root mesh; `clone(true)` for player / AI
 */
export function loadTruckModelTemplate(onToast) {
  if (typeof window !== "undefined" && window.location.protocol === "file:") {
    onToast?.("Serve over http(s) to load the truck");
    return Promise.resolve(null);
  }

  const pairs = [
    { mtl: "untitled_trianglefaced.mtl", obj: "untitled_trianglefaced.obj" },
    { mtl: "untitled_quardfaced.mtl", obj: "untitled_quardfaced.obj" }
  ];

  return tryLoadWithBases(onToast, pairs, 0, 0);
}

/**
 * @param {THREE.Object3D} parent
 * @param {(s: string) => void} onToast
 */
export function loadTruckModel(parent, onToast) {
  return loadTruckModelTemplate(onToast).then((m) => {
    if (m) parent.add(m.clone(true));
  });
}

/**
 * Tries (pair, base) combinations: pair index, then next base, then next pair.
 */
function tryLoadWithBases(onToast, pairs, pairIndex, baseIndex) {
  if (pairIndex >= pairs.length) {
    return loadObjNoMaterialWithBases(onToast, ["untitled_trianglefaced.obj", "untitled_quardfaced.obj"], 0, 0);
  }
  if (baseIndex >= ASSET_PATH_CANDIDATES.length) {
    return tryLoadWithBases(onToast, pairs, pairIndex + 1, 0);
  }

  const base = ASSET_PATH_CANDIDATES[baseIndex]();
  const { mtl, obj } = pairs[pairIndex];
  const mtlForDir = `${base}${mtl}`.replace(/\/+/g, "/");
  const dir = mtlSetPathForUrl(mtlForDir);

  const mtlLoader = new MTLLoader();
  mtlLoader.setPath(dir);
  return new Promise((resolve) => {
    mtlLoader.load(
      mtl,
      (loadedMaterials) => {
        loadedMaterials.preload();
        tuneMaterials(loadedMaterials.materials);
        const objLoader = new OBJLoader();
        objLoader.setMaterials(loadedMaterials);
        objLoader.setPath(dir);
        objLoader.load(
          obj,
          (model) => {
            prepareTruckModel(model);
            onToast?.("Truck model loaded");
            resolve(model);
          },
          undefined,
          () => {
            tryLoadWithBases(onToast, pairs, pairIndex, baseIndex + 1).then(resolve);
          }
        );
      },
      undefined,
      () => {
        tryLoadWithBases(onToast, pairs, pairIndex, baseIndex + 1).then(resolve);
      }
    );
  });
}

/**
 * @param {string[]} files — try each name across bases before giving up
 */
function loadObjNoMaterialWithBases(onToast, files, fileIndex, baseIndex) {
  if (fileIndex >= files.length) {
    onToast?.("Truck .obj not found (tried names and /truck-sim/ + /)");
    return Promise.reject(new Error("obj"));
  }
  if (baseIndex >= ASSET_PATH_CANDIDATES.length) {
    return loadObjNoMaterialWithBases(onToast, files, fileIndex + 1, 0);
  }
  const file = files[fileIndex];
  const base = ASSET_PATH_CANDIDATES[baseIndex]();
  const pathForDir = `${base}${file}`.replace(/\/+/g, "/");
  const dir = mtlSetPathForUrl(pathForDir);
  return new Promise((resolve) => {
    const objLoader = new OBJLoader();
    objLoader.setPath(dir);
    objLoader.load(
      file,
      (model) => {
        prepareTruckModel(model);
        onToast?.("Loaded mesh (no MTL in path)");
        resolve(model);
      },
      undefined,
      () => {
        loadObjNoMaterialWithBases(onToast, files, fileIndex, baseIndex + 1).then(resolve);
      }
    );
  });
}

/**
 * @param {THREE.Object3D} model
 */
function prepareTruckModel(model) {
  model.name = "TruckMesh";
  model.scale.setScalar(1.18);
  model.position.set(0, 0.2, 0.04);
  /* Authoring-neutral yaw; arcade mode applies π in `normalizeLoadedPlayerTruck` for −Z crawl direction. */
  model.rotation.y = 0;
  model.traverse((child) => {
    if (child.isMesh) {
      child.castShadow = true;
      child.receiveShadow = true;
    }
  });
}

function tuneMaterials(mats) {
  const colorMap = {
    bodycolour: 0xb3261e,
    head_paint: 0xb3261e,
    deep_black: 0x050505,
    diffuse_black: 0x111111,
    flat_black: 0x151515,
    gloss_black: 0x080808,
    tyres: 0x050505,
    wheels: 0xd6d7d2,
    wheel_hub: 0xc9cbc6,
    glass: 0x1b343b,
    full_glass: 0x1b343b,
    windglass: 0x19313a,
    light_emit: 0xfff1c1,
    signal_light: 0xff8a2c,
    full_glossy: 0xcfd1c9,
    door_knob: 0xd7d8d0,
    Material: 0x9b9b92
  };
  for (const [name, mat] of Object.entries(mats)) {
    if (!mat) continue;
    mat.side = THREE.FrontSide;
    mat.roughness = /glass|light|signal/i.test(name) ? 0.22 : 0.55;
    mat.metalness = /wheel|hub|gloss|knob|Material/i.test(name) ? 0.45 : 0.12;
    if (colorMap[name]) mat.color.setHex(colorMap[name]);
    if (/glass/i.test(name)) {
      mat.transparent = true;
      mat.opacity = 0.72;
    }
    if (/light|signal/i.test(name)) {
      mat.emissive = new THREE.Color(colorMap[name] || 0xffffff);
      mat.emissiveIntensity = 0.45;
    }
  }
}
